import { withErrorBoundary } from "@sentry/nextjs";
import cn from "classnames";
import { gql } from "graphql-request";
import { CSSProperties, VFC } from "react";
import styled from "styled-components";
import { Markdown } from "components";
import { useAnchor } from "hooks";
import { useContentHeight } from "hooks/useContentHeight";
import { ArrowDown } from "icons";
import { TickIcon } from "icons/TickIcon";
import { TableIraContent } from "types/graphql/generated";

interface Props {
  className?: string;
  content: TableIraContent;
}

const TableIra: VFC<Props> = withErrorBoundary(
  ({ content: { iraFeatures, rollovers, closeButtonLabel, openButtonLabel } }) => {
    const heightHook = useContentHeight(411);

    useAnchor("table-ira");

    return (
      <StyledTableIra
        className={cn("Content", "table-ira", {
          TableIraExpanded: heightHook.expanded,
        })}
        style={
          {
            "--content-wrapper-height": `${heightHook.contentHeight}px`,
          } as CSSProperties
        }
      >
        <div className="TableIra__content">
          <div className="TableIra__contentWrapper">
            <div className="TableIra__container" ref={heightHook.contentRef}>
              <ul className="TableIra__iraList">
                {iraFeatures.map(({ id, title, description, iraFeaturesList }) => (
                  <li key={id} className="TableIra__ira">
                    <h2 className="Font_24_32_600">{title}</h2>
                    <ul className="TableIra__featuresList">
                      {iraFeaturesList.map(({ id, feature }) => (
                        <li key={id} className="TableIra__feature">
                          <div className="TableIra__listItemIconContainer">
                            <TickIcon className="TableIra__listItemIcon" />
                          </div>

                          <Markdown className="TableIra__featureTitle Font_18_26_450">{feature}</Markdown>
                        </li>
                      ))}
                    </ul>
                    <Markdown className="TableIra__iraDescription Font_16_22_450">{description}</Markdown>
                  </li>
                ))}
                <li className="TableIra__rollovers">
                  <div className="TableIra__rolloversTitleWithSubTitle">
                    <h2 className="TableIra__rolloversTitle Font_24_32_600">{rollovers.title}</h2>
                    <p className="TableIra__rolloversSubTitle Font_18_26_450">{rollovers.subTitle}</p>
                  </div>

                  <Markdown className="TableIra__rolloversDescription Font_16_22_450">{rollovers.description}</Markdown>
                </li>
              </ul>
            </div>
            <button className="TableIra__collapseButton Font_18_26_600" onClick={heightHook.handleClick}>
              {heightHook.isCloseLabel ? closeButtonLabel : openButtonLabel}
              <ArrowDown
                className={cn("TableIra__buttonArrow", {
                  TableIra__buttonArrow_isExpanded: heightHook.isCloseLabel,
                })}
              />
            </button>
          </div>
        </div>
      </StyledTableIra>
    );
  },
  {}
);

const StyledTableIra = styled.section`
  .TableIra__contentWrapper {
    position: relative;
    height: 411px;
    overflow: hidden;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.fog["30"]};
    border-radius: 8px;
    transition: height 500ms ease-in-out 300ms;
  }

  .TableIra__container {
    padding: 64px 52px calc(56px + 74px) 48px;
  }

  .TableIra__iraList {
    display: flex;
  }

  .TableIra__iraList > * {
    flex: 1;
  }

  .TableIra__ira:nth-child(2) {
    margin-left: 48px;
  }

  .TableIra__featuresList {
    margin-top: 12px;
  }

  .TableIra__feature {
    display: flex;
    align-items: flex-start;
  }

  .TableIra__feature:not(:first-child) {
    margin-top: 12px;
  }

  .TableIra__listItemIconContainer {
    padding: 6px;
    line-height: 0;
    flex-shrink: 0;
  }

  .TableIra__listItemIcon {
    width: 16px;
    height: 16px;
  }

  .TableIra__featureTitle {
    margin-left: 8px;
  }

  .TableIra__iraDescription {
    margin-top: 32px;
    opacity: 0;
    transition: opacity 0.5s ease-out 0.2s;

    p:not(:first-child) {
      margin-top: 22px;
    }
  }

  .TableIra__rollovers {
    display: flex;
    flex-direction: column;
    margin-left: 96px;
  }

  .TableIra__rolloversTitleWithSubTitle {
    border-left: 1px solid ${({ theme }) => theme.colors.fog["30"]};
    padding-left: 48px;
    margin-left: -48px;
    padding-bottom: 14px;
  }

  .TableIra__rolloversSubTitle {
    margin-top: 12px;
  }

  .TableIra__rolloversDescription {
    flex-grow: 1;
    margin-left: -48px;
    padding-left: 48px;
    padding-top: 24px;
    border-left: 1px solid ${({ theme }) => theme.colors.fog["30"]};
    opacity: 0;
    transition: opacity 0.5s ease-out 0.2s;

    p:not(:first-child) {
      margin-top: 22px;
    }
  }

  .TableIra__collapseButton {
    position: absolute;
    width: 100%;
    height: 76px;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 14px 24px;
    border-radius: 0 0 8px 8px;
    background-color: #d0e6e0;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.fog["30"]};
    cursor: pointer;
  }

  .TableIra__buttonArrow {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    margin-left: 15px;
    transform: none;
  }

  .TableIra__buttonArrow_isExpanded {
    transform: rotate(180deg);
  }

  &.TableIraExpanded {
    .TableIra__contentWrapper {
      height: var(--content-wrapper-height);
    }

    .TableIra__iraDescription {
      opacity: 1;
    }

    .TableIra__rolloversDescription {
      opacity: 1;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .TableIra__container {
      padding-left: 28px;
      padding-right: 24px;
    }

    .TableIra__rolloversDescription {
      padding-top: 32px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .TableIra__container {
      padding-left: 16px;
      padding-right: 16px;
    }

    .TableIra__iraDescription {
      margin-top: 32px;
      transition: opacity 0.2s ease-out 0.4s;
    }

    .TableIra__iraList {
      flex-direction: column;
    }

    .TableIra__ira:nth-child(1) {
      .TableIra__featuresList {
        margin-top: 12px;
      }
    }

    .TableIra__ira:nth-child(2) {
      margin-left: 0;
      margin-top: 32px;
      opacity: 0;
      transition: opacity 0.2s ease-out 0.4s;
    }

    .TableIra__rollovers {
      margin-left: 0;
      margin-top: 48px;
      padding-left: 0;
      padding-top: 48px;
      border-top: 1px solid ${({ theme }) => theme.colors.fog["30"]};
      border-left: none;
      opacity: 0;
      transition: opacity 0.2s ease-out 0.4s;
    }

    .TableIra__rolloversTitleWithSubTitle {
      padding-left: 0;
      margin-left: 0;
      border-left: none;
    }

    .TableIra__rolloversDescription {
      margin-left: 0;
      padding-left: 0;
      border-left: none;
    }

    &.TableIraExpanded {
      .TableIra__rollovers {
        opacity: 1;
        border-left: 0;
      }

      .TableIra__ira:nth-child(2) {
        opacity: 1;
      }
    }
  }
`;

gql`
  fragment TableIraContent on ComponentTableTableIra {
    iraFeatures {
      id
      title
      description
      iraFeaturesList {
        id
        feature
      }
    }
    rollovers {
      title
      subTitle
      description
    }
    openButtonLabel
    closeButtonLabel
  }
`;

export { TableIra };
export type { Props as TableIraProps };
